import { useState, useEffect, useRef } from 'react';
import { useTooltips } from '../context/TooltipContext';

/**
 * Renders a small gold '?' button that shows a help-text popup.
 * Usage: <Tooltip tooltipKey="song_title" />
 *
 * • Desktop: hover to show, mouse-leave to hide
 * • Mobile:  tap to toggle open/closed
 * • Click outside the wrapper closes the popup
 * • Returns null if the key is not found in TooltipContext (no visual noise)
 */
export default function Tooltip({ tooltipKey, text: inlineText }) {
  const tooltips = useTooltips();
  const text     = inlineText || tooltips[tooltipKey];

  const [open, setOpen] = useState(false);
  const wrapRef         = useRef(null);

  // Close on outside click / tap
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Render nothing if the key isn't seeded yet or doesn't exist
  if (!text) return null;

  return (
    <span
      ref={wrapRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      style={{
        position:       'relative',
        display:        'inline-flex',
        alignItems:     'center',
        verticalAlign:  'middle',
      }}
    >
      {/* Gold '?' button */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label="Help"
        aria-expanded={open}
        style={{
          width:           '18px',
          height:          '18px',
          borderRadius:    '50%',
          background:      '#C9A84C',
          color:           '#1B2B5B',
          border:          'none',
          cursor:          'pointer',
          fontSize:        '11px',
          fontWeight:      700,
          lineHeight:      1,
          display:         'inline-flex',
          alignItems:      'center',
          justifyContent:  'center',
          margin:          '0 4px',
          flexShrink:      0,
          transition:      'background 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#b8962e'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = '#C9A84C'; }}
      >
        ?
      </button>

      {/* Popup bubble */}
      {open && (
        <span
          role="tooltip"
          style={{
            position:     'absolute',
            bottom:       'calc(100% + 8px)',
            left:         '50%',
            transform:    'translateX(-50%)',
            background:   '#1B2B5B',
            color:        '#fff',
            borderRadius: '8px',
            padding:      '10px 14px',
            maxWidth:     '280px',
            width:        'max-content',
            fontSize:     '0.78rem',
            lineHeight:   1.5,
            boxShadow:    '0 4px 16px rgba(0,0,0,0.25)',
            zIndex:       999,
            pointerEvents:'none',
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}
